import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Badge, Button, Col, Input, Row } from "reactstrap"
import Select from "react-select"
import { toast } from "react-toastify"

import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import ConfirmModal from "components/Common/ConfirmModal"
import { formatUiDate } from "utils/formatDate"
import { useListParams } from "hooks/useListParams"
import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"
import {
  useDeleteLead,
  useLeadMeta,
  useLeads,
  useMyLeads,
} from "hooks/leadHook"
import LeadFormModal from "./LeadFormModal"

const STATUS_COLORS = {
  new: "secondary",
  contacted: "info",
  qualified: "primary",
  proposal: "warning",
  negotiation: "warning",
  won: "success",
  lost: "danger",
}

const PRIORITY_COLORS = { low: "light", medium: "info", high: "danger" }

const titleCase = s =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())

const LeadList = () => {
  document.title = "Leads"

  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canViewAll = hasPermission(ACCESS.LEAD.VIEW_LIST)
  const canCreate = hasPermission(ACCESS.LEAD.CREATE)
  const canEdit = hasPermission(ACCESS.LEAD.EDIT)
  const canDelete = hasPermission(ACCESS.LEAD.DELETE)

  const { params, setPage } = useListParams({ page: 1 })
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState(null)
  const [source, setSource] = useState(null)
  const [priority, setPriority] = useState(null)
  const [assignee, setAssignee] = useState(null)
  const [overdue, setOverdue] = useState(false)
  const [scope, setScope] = useState(canViewAll ? "all" : "mine")
  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const { data: metaResp } = useLeadMeta()
  const meta = metaResp?.data ?? {}

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      ...(search ? { q: search } : {}),
      ...(status ? { status } : {}),
      ...(source ? { source } : {}),
      ...(priority ? { priority } : {}),
      ...(assignee ? { assigned_to: assignee } : {}),
      ...(overdue ? { overdue: 1 } : {}),
    }),
    [params.page, search, status, source, priority, assignee, overdue]
  )

  const useAll = scope === "all" && canViewAll
  const allQuery = useLeads(queryParams, useAll)
  const myQuery = useMyLeads(queryParams, !useAll)
  const { data: resp, isLoading, isFetching, refetch } = useAll ? allQuery : myQuery

  const rows = resp?.data?.data ?? []
  const paginationInfo = resp?.data

  const deleteMutation = useDeleteLead()

  const statusOptions = (meta.statuses ?? []).map(s => ({ value: s, label: titleCase(s) }))
  const sourceOptions = (meta.sources ?? []).map(s => ({ value: s, label: titleCase(s) }))
  const priorityOptions = (meta.priorities ?? []).map(p => ({ value: p, label: titleCase(p) }))
  const assigneeOptions = (meta.assignees ?? []).map(e => ({ value: e.id, label: e.name }))

  const columns = useMemo(
    () => [
      {
        header: "Lead",
        cell: ({ row }) => (
          <div>
            <div className="fw-medium">{row.original.name}</div>
            <small className="text-muted">
              {row.original.luid}
              {row.original.interested_in ? ` · ${row.original.interested_in}` : ""}
            </small>
          </div>
        ),
      },
      { header: "Contact", accessorKey: "mobile" },
      {
        header: "Source",
        cell: ({ row }) => titleCase(row.original.source),
      },
      {
        header: "Assignee",
        cell: ({ row }) => row.original.assignee?.name ?? "—",
      },
      {
        header: "Status",
        cell: ({ row }) => (
          <Badge color={STATUS_COLORS[row.original.status] ?? "light"}>
            {titleCase(row.original.status)}
          </Badge>
        ),
      },
      {
        header: "Priority",
        cell: ({ row }) => (
          <Badge color={PRIORITY_COLORS[row.original.priority] ?? "light"} className="text-dark">
            {titleCase(row.original.priority)}
          </Badge>
        ),
      },
      {
        header: "Follow-up",
        cell: ({ row }) =>
          row.original.next_follow_up_at
            ? formatUiDate(row.original.next_follow_up_at)
            : "—",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <Button
              size="sm"
              color="primary"
              outline
              onClick={() => navigate(`/leads/${row.original.id}`)}
            >
              <i className="mdi mdi-eye-outline" />
            </Button>
            {canEdit && (
              <Button
                size="sm"
                color="secondary"
                outline
                onClick={() => setEditTarget(row.original)}
              >
                <i className="mdi mdi-pencil-outline" />
              </Button>
            )}
            {canDelete && (
              <Button
                size="sm"
                color="danger"
                outline
                onClick={() => setDeleteTarget(row.original)}
              >
                <i className="mdi mdi-trash-can-outline" />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [navigate, canEdit, canDelete]
  )

  return (
    <PageWrapper title="Leads" breadcrumbItem="Lead Management">
      <Row className="mb-3 g-2 align-items-center">
        {canViewAll && (
          <Col md="auto">
            <div className="btn-group btn-group-sm" role="group">
              <Button
                color={scope === "all" ? "primary" : "light"}
                onClick={() => setScope("all")}
              >
                All
              </Button>
              <Button
                color={scope === "mine" ? "primary" : "light"}
                onClick={() => setScope("mine")}
              >
                My leads
              </Button>
            </div>
          </Col>
        )}
        <Col md={3}>
          <Input
            type="search"
            placeholder="Search name, mobile, ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Select
            options={statusOptions}
            value={statusOptions.find(o => o.value === status) ?? null}
            onChange={o => setStatus(o?.value ?? null)}
            placeholder="Status"
            isClearable
          />
        </Col>
        <Col md={2}>
          <Select
            options={sourceOptions}
            value={sourceOptions.find(o => o.value === source) ?? null}
            onChange={o => setSource(o?.value ?? null)}
            placeholder="Source"
            isClearable
          />
        </Col>
        <Col md={2}>
          <Select
            options={priorityOptions}
            value={priorityOptions.find(o => o.value === priority) ?? null}
            onChange={o => setPriority(o?.value ?? null)}
            placeholder="Priority"
            isClearable
          />
        </Col>
        {canViewAll && (
          <Col md={2}>
            <Select
              options={assigneeOptions}
              value={assigneeOptions.find(o => o.value === assignee) ?? null}
              onChange={o => setAssignee(o?.value ?? null)}
              placeholder="Assignee"
              isClearable
            />
          </Col>
        )}
        <Col md="auto">
          <div className="form-check">
            <Input
              type="checkbox"
              id="overdue-filter"
              checked={overdue}
              onChange={e => setOverdue(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="overdue-filter">
              Overdue follow-up
            </label>
          </div>
        </Col>
        <Col className="text-end">
          <Button color="light" onClick={() => refetch()} disabled={isFetching} className="me-2">
            <i className={`mdi mdi-refresh ${isFetching ? "mdi-spin" : ""}`} />
          </Button>
          {canCreate && (
            <Button color="primary" onClick={() => setFormOpen(true)}>
              <i className="mdi mdi-plus" /> Add lead
            </Button>
          )}
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={rows}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isFetching}
        className="table-responsive"
      />

      {(formOpen || editTarget) && (
        <LeadFormModal
          isOpen={formOpen || Boolean(editTarget)}
          lead={editTarget}
          meta={meta}
          toggle={() => {
            setFormOpen(false)
            setEditTarget(null)
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          isOpen={Boolean(deleteTarget)}
          toggle={() => setDeleteTarget(null)}
          title="Delete lead"
          message={`Delete lead "${deleteTarget.name}"? This can be restored by an administrator.`}
          confirmLabel="Delete"
          btnColor="danger"
          icon="mdi-trash-can-outline"
          isConfirming={deleteMutation.isPending}
          onConfirm={async () => {
            try {
              await deleteMutation.mutateAsync(deleteTarget.id)
              toast.success("Lead deleted")
              setDeleteTarget(null)
            } catch (err) {
              toast.error(err?.response?.data?.message || "Delete failed")
            }
          }}
        />
      )}
    </PageWrapper>
  )
}

export default LeadList
